import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SEO, { SEO_DATA } from '../../components/SEO';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Check, ArrowRight, GraduationCap, Briefcase, Star, BookOpen } from 'lucide-react';

export default function EntryLevelPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO {...SEO_DATA.entryLevel} />
      <Header />
      
      <main className="pt-24 pb-20">
        {/* Hero */}
        <section className="bg-gradient-hero py-20">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <Badge className="bg-white/10 text-white border-white/20 mb-6">New to Work</Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit' }}>
              Resume Help for Entry-Level Job Seekers
            </h1>
            <p className="text-white/80 text-lg max-w-2xl mx-auto mb-8">
              Limited work experience doesn't mean a weak resume. We help new graduates and entry-level candidates present their skills, education, and potential effectively.
            </p>
            <Button 
              onClick={() => navigate('/start')}
              className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-8 py-6 text-lg"
            >
              Start Your Intake <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </section>

        {/* Challenges */}
        <section className="max-w-4xl mx-auto px-6 py-16">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-8" style={{ fontFamily: 'Outfit' }}>
            Entry-Level Resume Challenges
          </h2>
          
          <div className="space-y-4">
            {[
              { problem: 'Little or no professional experience', fix: 'We highlight internships, projects, volunteer work, and relevant coursework.' },
              { problem: 'Don\'t know what to include', fix: 'We identify the most relevant experiences and present them professionally.' },
              { problem: 'Resume looks empty', fix: 'We maximize impact with proper formatting and emphasis on transferable skills.' },
              { problem: 'Competing against experienced candidates', fix: 'We position your potential, adaptability, and fresh perspective as strengths.' },
            ].map((item, i) => (
              <Card key={i} className="p-6 rounded-2xl border border-slate-200">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-amber-600 font-bold">?</span>
                  </div>
                  <div>
                    <p className="font-medium text-slate-900 mb-1">{item.problem}</p>
                    <p className="text-slate-600 text-sm">→ {item.fix}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </section>

        {/* What We Include */}
        <section className="bg-white py-16">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-slate-900 text-center mb-12" style={{ fontFamily: 'Outfit' }}>
              What We Highlight for Entry-Level Resumes
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: GraduationCap, title: 'Education & Coursework', desc: 'Relevant courses, GPA (if strong), academic projects, and certifications.' },
                { icon: Briefcase, title: 'Internships & Part-Time Jobs', desc: 'Maximize the impact of any work experience, even retail or food service.' },
                { icon: Star, title: 'Projects & Achievements', desc: 'Academic projects, personal projects, competitions, and awards.' },
                { icon: BookOpen, title: 'Volunteer Experience', desc: 'Community involvement and volunteer work that shows character and skills.' },
                { icon: Check, title: 'Transferable Skills', desc: 'Communication, teamwork, problem-solving, and technical skills.' },
                { icon: Check, title: 'Professional Formatting', desc: 'Clean, ATS-friendly format that looks professional despite limited experience.' },
              ].map((item, i) => (
                <Card key={i} className="p-6 rounded-2xl border border-slate-200">
                  <item.icon className="h-8 w-8 text-teal-600 mb-4" />
                  <h3 className="font-semibold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-slate-600 text-sm">{item.desc}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Who This Is For */}
        <section className="max-w-4xl mx-auto px-6 py-16">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-8" style={{ fontFamily: 'Outfit' }}>
            Who This Service Is For
          </h2>
          
          <div className="grid md:grid-cols-2 gap-4">
            {[
              'Recent college graduates',
              'High school graduates entering the workforce',
              'Students looking for internships',
              'Career starters with limited experience',
              'People re-entering the workforce',
              'Those with only part-time or retail experience',
              'Candidates targeting their first "real" job',
              'Anyone struggling with a thin resume',
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl">
                <Check className="h-5 w-5 text-teal-600 flex-shrink-0" />
                <span className="text-slate-700">{item}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Tips */}
        <section className="bg-slate-100 py-16">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-slate-900 text-center mb-8" style={{ fontFamily: 'Outfit' }}>
              Entry-Level Resume Tips
            </h2>
            
            <div className="space-y-4">
              {[
                'Place education near the top if you\'re a recent graduate',
                'Include relevant coursework, especially for technical or specialized roles',
                'Don\'t dismiss retail, food service, or part-time jobs—they show work ethic and soft skills',
                'Academic projects count as experience if you describe them professionally',
                'Volunteer work and extracurriculars demonstrate leadership and initiative',
                'Focus on skills and potential rather than years of experience',
              ].map((tip, i) => (
                <div key={i} className="flex items-start gap-3 bg-white p-4 rounded-xl">
                  <Check className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">{tip}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related Pages */}
        <section className="max-w-4xl mx-auto px-6 py-16">
          <h2 className="text-2xl font-bold text-slate-900 text-center mb-8" style={{ fontFamily: 'Outfit' }}>
            Explore More
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/for/career-change" className="text-teal-700 hover:text-teal-800 underline">Career Changers</Link>
            <span className="text-slate-300">|</span>
            <Link to="/for/customer-service" className="text-teal-700 hover:text-teal-800 underline">Customer Service Jobs</Link>
            <span className="text-slate-300">|</span>
            <Link to="/services/resume-rewrite" className="text-teal-700 hover:text-teal-800 underline">Resume Rewrite Service</Link>
            <span className="text-slate-300">|</span>
            <Link to="/pricing" className="text-teal-700 hover:text-teal-800 underline">View Pricing</Link>
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-3xl mx-auto px-6 py-16 text-center">
          <Card className="p-8 rounded-3xl bg-slate-900 text-white">
            <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit' }}>
              Ready to build a stronger entry-level resume?
            </h3>
            <p className="text-slate-300 mb-6">
              Submit your intake and tell us about your background. We'll help you present your potential professionally.
            </p>
            <Button 
              onClick={() => navigate('/start')}
              className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-8"
            >
              Start Your Intake <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Card>
        </section>
      </main>

      <Footer />
    </div>
  );
}
